import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/page/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon from '../shared/WikiIcon';
import { WikiLoading } from '../shared/WikiStates';
import useWikiRole from '../shared/useWikiRole';
import { wikiPaths } from '../shared/wikiRoutes';
import BrainTreeTab from './tree/BrainTreeTab';
import BrainReviewTab from './review/BrainReviewTab';
import BrainInsightsTab from './insights/BrainInsightsTab';
import BrainSettingsTab from './settings/BrainSettingsTab';
import {
    BRAIN_DESCRIPTION, BRAIN_GATE, BRAIN_PERM_NOTE, BRAIN_TABS, BRAIN_TITLE,
} from './tree/brainCopy';
import { brainTreeState } from './tree/brainTreeState';
import { brainPendingCount, useBrainHealth } from './tree/useBrainTree';
import './brain.css';

const TAB_COMPONENTS = {
    tree: BrainTreeTab,
    review: BrainReviewTab,
    insights: BrainInsightsTab,
    settings: BrainSettingsTab,
};

/**
 * The header's first pill: the prototype shows "JEV connected · jev-1.13.0";
 * the real build shows the honest JEV state (contract §7).
 */
function jevPill(jev) {
    if (!jev) return null;
    const version = jev.version ? ` · ${jev.version}` : '';
    if (jev.status === 'connected') return { tone: 'jev', label: `JEV connected${version}` };
    if (jev.status === 'unavailable') return { tone: 'jev-warn', label: 'JEV unavailable' };
    return { tone: 'jev-off', label: 'JEV off' };
}

/** Member gate (prototype `brGate`): replaces the whole Brain page. */
function BrainGate() {
    return (
        <WikiFrame className="wk-brain" testId="brain-gate">
            <div className="wk-brain-gate">
                <div className="wk-brain-gate-inner">
                    <span className="wk-brain-gate-icon" aria-hidden="true"><WikiIcon name="lock" size={22} /></span>
                    <h2 className="wk-t-title wk-brain-gate-title">{BRAIN_GATE.title}</h2>
                    <p className="wk-t-body wk-brain-gate-body">{BRAIN_GATE.body}</p>
                </div>
            </div>
        </WikiFrame>
    );
}

/**
 * AI Brain page shell (E1 §1.2): header (brain tile, PageHeader title +
 * description, three pills), Editor read-only banner, the four tabs with the
 * always-on amber Review-queue count, then the active tab. Members get the gate
 * (§1.7). Tabs are URL-addressable: /wiki/brain, /review, /insights, /settings.
 *
 * Props: `tab` ∈ 'tree'|'review'|'insights'|'settings' (from pages/WikiV2.jsx).
 * Tab props contract: notes/FE-brain-tree.md §1.
 */
export default function BrainPage({ tab = 'tree' }) {
    const wikiRole = useWikiRole();
    const navigate = useNavigate();
    const { user } = useAuth() || {};
    const canView = !wikiRole.loading && wikiRole.canBrainView;
    const health = useBrainHealth({ enabled: canView });
    const activeTab = TAB_COMPONENTS[tab] ? tab : 'tree';

    useEffect(() => {
        if (canView) brainTreeState.rememberTab(activeTab);
    }, [canView, activeTab]);

    const openTab = useCallback((id) => {
        navigate(wikiPaths.brain(TAB_COMPONENTS[id] ? id : 'tree'));
    }, [navigate]);

    if (wikiRole.loading) {
        return (
            <WikiFrame className="wk-brain">
                <WikiLoading label="Checking Wiki access…" />
            </WikiFrame>
        );
    }
    if (!wikiRole.canBrainView) return <BrainGate />;

    const canManage = Boolean(wikiRole.canBrainManage);
    const readOnly = !canManage;
    const roleId = canManage ? 'admin' : 'editor';
    const roleLabel = canManage ? 'Wiki admin' : 'Editor';
    const permNote = canManage ? BRAIN_PERM_NOTE.manager : BRAIN_PERM_NOTE.viewer;
    const pendingCount = brainPendingCount(health.data);
    const jev = jevPill(health.data?.jev);
    const orgName = user?.org_name || 'your workspace';
    const Tab = TAB_COMPONENTS[activeTab];

    return (
        <WikiFrame className="wk-brain" testId="brain-page">
            <div className="wk-brain-top">
                <div className="wk-brain-head">
                    <span className="wk-brain-tile" aria-hidden="true"><WikiIcon name="brain" size={22} /></span>
                    <div className="wk-brain-head-text">
                        <PageHeader title={BRAIN_TITLE} subtitle={BRAIN_DESCRIPTION} testId="brain-header" />
                    </div>
                    <div className="wk-brain-pills" data-testid="brain-pills">
                        {jev && (
                            <span className={`wk-brain-pill wk-brain-pill--${jev.tone}`} data-testid="brain-jev-pill">
                                <span className="wk-brain-pill-dot" aria-hidden="true" />
                                {jev.label}
                            </span>
                        )}
                        <span className="wk-brain-pill wk-brain-pill--lock">
                            <WikiIcon name="lock" size={12} />
                            {`Learning stays inside ${orgName}`}
                        </span>
                        <span className="wk-brain-pill wk-brain-pill--role" title={permNote} data-testid="brain-role-pill">
                            <WikiIcon name="key-round" size={12} />
                            {`Viewing as ${roleLabel}`}
                        </span>
                    </div>
                </div>
                {readOnly && (
                    <div className="wk-brain-readonly" role="note" data-testid="brain-readonly">
                        <WikiIcon name="eye" size={15} />
                        <span>{permNote}</span>
                    </div>
                )}
                <div className="wk-brain-tabs" role="tablist" aria-label={BRAIN_TITLE}>
                    {BRAIN_TABS.map((t) => {
                        const on = t.id === activeTab;
                        const count = t.id === 'review' && pendingCount > 0 ? pendingCount : 0;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={on}
                                className={`wk-brain-tab${on ? ' is-active' : ''}`}
                                data-testid={`brain-tab-${t.id}`}
                                onClick={() => { if (!on) openTab(t.id); }}
                            >
                                {t.label}
                                {count > 0 && (
                                    <span className="wk-count wk-count--amber" data-testid="brain-tab-count">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="wk-brain-body" role="tabpanel" aria-label={BRAIN_TABS.find((t) => t.id === activeTab).label}>
                <Tab
                    role={wikiRole}
                    roleId={roleId}
                    roleLabel={roleLabel}
                    canManage={canManage}
                    readOnly={readOnly}
                    canInvestigate={Boolean(wikiRole.canInvestigate)}
                    pendingCount={pendingCount}
                    onOpenTab={openTab}
                />
            </div>
        </WikiFrame>
    );
}
